import * as React from 'react';
import classnames from 'classnames';

import { Link } from 'libs/router';

import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import Tooltip from 'components/Tooltip';

import ListsContextMenu from 'modules/lists/components/context-menu/ListsContextMenu';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { abbrNum } from 'utils/helper/common';
import { getEntityFromProfile } from 'utils/view';

import type { List, ListsContentType } from 'types';

import './lists-dashboard-item.scss';

applyTranslations(require('./lists-dashboard-item.i18n.json'));

type Props = {
  contentType: ListsContentType | undefined | null,
  isManageableByOrganization?: boolean,
  item: List,
  orgId: number,
  canManageList: boolean,
  onDelete: () => void,
  onDuplicate?: () => void
};

class ListsDashboardItem extends React.PureComponent<Props> {
  renderBrandDetails(): React.ReactNode {
    const { item } = this.props;
    const brandProfile = item.brandProfile;
    const url = brandProfile?.id ? `/brand/profiles/${brandProfile.id}` : null;
    const brandName = brandProfile?.name;
    const entity = getEntityFromProfile(brandProfile);

    return (
      <div className="lists-dashboard-item__brand-details">
        <div
          className="lists-dashboard-item__avatar"
        >
          <EntityAvatar
            key={item.id}
            entity={entity}
            link={url}
            customSize={{
              width: 32,
              height: 32
            }}
            openInNewTab
            hideTitle
            bordered
          />
        </div>
        <Link
          className="lists-dashboard-item__brand-name text-overflow-ellipsis"
          title={brandName}
          to={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {brandName}
        </Link>
      </div>
    );
  }

  renderListDetails() {
    const { item, orgId, contentType } = this.props;
    const listUrl = (contentType === 'content-list')
      ? `/collections/content/${item.smartListType || item.id}?orgId=${orgId}`
      : `/collections/creators/${item.id}?prev=lists&orgId=${orgId}`;
    const nameAndDateClasses = classnames({
      'lists-dashboard-item__name-and-date': true,
      'lists-dashboard-item__name-and-date--with-description': item.description.length > 0
    });

    return (
      <div className="lists-dashboard-item__list-details">
        <div className={nameAndDateClasses}>
          <Link
            className="lists-dashboard-item__name text-overflow-ellipsis"
            to={listUrl}
          >
            {item.label}
          </Link>
          <div className="lists-dashboard-item__date">
            {`${__t('lists_dashboard_item.label_updated')}: ${getDisplayDate(item.updatedAt)}`}
          </div>
        </div>
        {item.description && (
          <div className="lists-dashboard-item__description-container">
            <Tooltip
              textAlign="center"
              width="long"
              tooltipDirection={{
                vertical: 'top',
                horizontal: 'right'
              }}
              message={item.description}
            >
              <span className="lists-dashboard-item__description">
                {item.description}
              </span>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }

  renderMetrics(): React.ReactNode {
    const { item, contentType } = this.props;

    if (contentType === 'content-list') {
      return this.renderContentListMetrics();
    }

    return (
      <div className="lists-dashboard-item__metrics">
        <div className="lists-dashboard-item__metric">
          <span className="lists-dashboard-item__metric-value">
            {item.numCreators}
          </span>
          <div className="lists-dashboard-item__metric-label">
            {__t('lists_dashboard_item.label_creators')}
          </div>
        </div>
        <div className="lists-dashboard-item__metric">
          <span className="lists-dashboard-item__metric-value">
            {abbrNum(item.totalFollowers)}
          </span>
          <div className="lists-dashboard-item__metric-label">
            {__t('lists_dashboard_item.label_followers')}
          </div>
        </div>
      </div>
    );
  }

  renderContentListMetrics(): React.ReactNode {
    const { item } = this.props;

    return (
      <div className="lists-dashboard-item__metrics lists-dashboard-item__metrics--content-list">
        <div className="lists-dashboard-item__metric">
          <span className="lists-dashboard-item__metric-value">
            {abbrNum(item.numContentPieces)}
          </span>
          <div className="lists-dashboard-item__metric-label">
            {__t('lists_dashboard_item.label_content_pieces')}
          </div>
        </div>
        <div className="lists-dashboard-item__metric">
          <span className="lists-dashboard-item__metric-value">
            {abbrNum(item.numCreators)}
          </span>
          <div className="lists-dashboard-item__metric-label">
            {__t('lists_dashboard_item.label_creators')}
          </div>
        </div>
      </div>
    );
  }

  renderContextMenu(): React.ReactNode {
    const { orgId, item, canManageList, contentType, onDelete, onDuplicate, isManageableByOrganization } = this.props;

    if (contentType === 'content-list') {
      return null;
    }

    return (
      <div className="lists-dashboard-item__context-menu">
        <ListsContextMenu
          key={item.id}
          orgId={orgId}
          list={item}
          canManageList={canManageList}
          isManageableByOrganization={isManageableByOrganization}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { contentType } = this.props;
    const itemClassNames = classnames('lists-dashboard-item', {
      'lists-dashboard-item--no-context-menu': contentType === 'content-list'
    });

    return (
      <div className={itemClassNames}>
        {this.renderBrandDetails()}
        {this.renderListDetails()}
        {this.renderMetrics()}
        {this.renderContextMenu()}
      </div>
    );
  }
}

export default ListsDashboardItem;
